import { memo, Profiler, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Group } from '@mantine/core';
import { ChevronLeft } from 'lucide-react';
import { useHasPermission } from '../../../providers/AuthProvider';
import { useAdminUser } from '../../../hooks/useAdminUser';
import { UserInfoSection } from '../../../admin/components/UserInfoSection';
import { LazyProductsTab } from '../../../admin/components/LazyComponents';
import { LazyCourseEditModal } from '../../../admin/components/LazyComponents';

const AdminUserProducts = memo(() => {
  const hasManageProducts = useHasPermission('manage_products');
  const { id } = useParams<{ id: string }>();

  // Get admin user data and actions
  const adminUserData = useAdminUser();

  // TESTING PURPOSES ONLY
  const onRenderCallback = useCallback(
    (id: string, phase: string, actualDuration: number) => {
      if (actualDuration > 8) {
        // More aggressive threshold - half a frame
        console.warn(
          `🐌 Slow render detected in ${id}: ${phase} took ${actualDuration}ms`
        );
      }
    },
    []
  );

  if (!hasManageProducts) {
    return <div>Non hai i permessi per gestire i prodotti</div>;
  }

  return (
    <Profiler id='AdminUserProducts' onRender={onRenderCallback}>
      <Group mb='md'>
        <Button
          variant='subtle'
          leftSection={<ChevronLeft size={16} />}
          component={Link}
          to={`/admin/users/${id}`}
        >
          Torna al Profilo Utente
        </Button>
      </Group>

      <UserInfoSection
        userInfo={adminUserData.userInfo}
        loadingUserInfo={adminUserData.loadingUserInfo}
      />

      <Profiler id='ProductsTab' onRender={onRenderCallback}>
        <LazyProductsTab
          userProducts={adminUserData.userProducts}
          loadingProducts={adminUserData.loadingProducts}
          userId={adminUserData.userId}
          onRefresh={adminUserData.refreshProducts}
          onUpdateProduct={adminUserData.updateProduct}
          onRemoveProduct={adminUserData.removeProduct}
          deleteInProgress={adminUserData.deleteInProgress}
        />
      </Profiler>

      <LazyCourseEditModal
        opened={adminUserData.selectedUserCourse !== undefined}
        onClose={() => adminUserData.setSelectedUserCourse(undefined)}
        course={adminUserData.selectedUserCourse}
        onSave={adminUserData.handleUpdateCourse}
      />
    </Profiler>
  );
});

AdminUserProducts.displayName = 'AdminUserProducts';

export default AdminUserProducts;
